import React, { useState, useEffect } from 'react';
import { Download, RefreshCw, Check, Smartphone, Share2, Plus, X } from 'lucide-react';
import '../styles/AppActions.css';

/**
 * Floating helper buttons (used in Footer):
 *  - "Install App"   → re-trigger PWA install prompt or show iOS instructions
 *  - "Check for Updates" → force-clear all service-worker caches + reload to fetch latest version
 *
 * Always rendered. Hides "Install App" only when the app is already running standalone.
 */
const AppActions = () => {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [isIOS, setIsIOS] = useState(false);
  const [isStandalone, setIsStandalone] = useState(false);
  const [showIOSModal, setShowIOSModal] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [refreshDone, setRefreshDone] = useState(false);
  const [version, setVersion] = useState('');

  useEffect(() => {
    setIsIOS(/iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream);
    setIsStandalone(
      window.matchMedia('(display-mode: standalone)').matches ||
      window.navigator.standalone === true
    );

    const handler = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
    };
    window.addEventListener('beforeinstallprompt', handler);

    // Read SW version from /service-worker.js header for display
    fetch('/service-worker.js?probe=' + Date.now(), { cache: 'no-store' })
      .then((r) => r.text())
      .then((txt) => {
        const m = txt.match(/cadence-medical-(v\d+)/);
        if (m) setVersion(m[1]);
      })
      .catch(() => {});

    return () => window.removeEventListener('beforeinstallprompt', handler);
  }, []);

  const handleInstallClick = async () => {
    // Clear any prior dismissal so the system prompt cooperates
    localStorage.removeItem('cadence-pwa-dismissed');

    if (isIOS) {
      setShowIOSModal(true);
      return;
    }
    if (deferredPrompt) {
      deferredPrompt.prompt();
      const { outcome } = await deferredPrompt.userChoice;
      if (outcome === 'accepted') {
        setDeferredPrompt(null);
      }
    } else {
      // No native prompt available — show fallback modal with manual instructions
      setShowIOSModal(true);
    }
  };

  const handleRefresh = async () => {
    setIsRefreshing(true);
    try {
      // 1. Delete every cache entry (Cache Storage API).
      //    Do NOT unregister the service worker — that would destroy any
      //    active push subscriptions and force the user to re-enable.
      if ('caches' in window) {
        const keys = await caches.keys();
        await Promise.all(keys.map((k) => caches.delete(k)));
      }
      // 2. Ask the active service worker to fetch the latest SW from the network
      //    and activate it immediately. Handle BOTH "installing" and "waiting"
      //    states so a fresh SW that hasn't reached "waiting" yet still gets
      //    SKIP_WAITING posted as soon as it transitions.
      if ('serviceWorker' in navigator) {
        try {
          const reg = await navigator.serviceWorker.getRegistration();
          if (reg) {
            await reg.update(); // forces network check for new SW

            const sendSkipWaiting = (worker) => {
              if (!worker) return;
              if (worker.state === 'installed') {
                worker.postMessage({ type: 'SKIP_WAITING' });
              } else {
                worker.addEventListener('statechange', () => {
                  if (worker.state === 'installed') {
                    worker.postMessage({ type: 'SKIP_WAITING' });
                  }
                });
              }
            };

            // Whichever slot the new SW lands in, push it to active.
            if (reg.waiting) sendSkipWaiting(reg.waiting);
            if (reg.installing) sendSkipWaiting(reg.installing);

            // Wait briefly for the new SW to take control before reloading
            await new Promise((resolve) => setTimeout(resolve, 600));
          }
        } catch (e) {
          console.warn('[CADENCE] SW update failed:', e);
        }
      }
      // 3. Clear update-flag localStorage keys (preserve auth + push prefs)
      const keysToWipe = ['cadence-pwa-dismissed', 'cadence-update-dismissed'];
      keysToWipe.forEach((k) => localStorage.removeItem(k));

      setRefreshDone(true);
      setTimeout(() => {
        // Hard reload bypassing the cache so even the HTML shell is fresh
        window.location.reload(true);
      }, 600);
    } catch (err) {
      console.error('Refresh failed:', err);
      setIsRefreshing(false);
    }
  };

  return (
    <>
      <div className="app-actions-row" data-testid="app-actions-row">
        {!isStandalone && (
          <button
            type="button"
            className="app-action-btn"
            onClick={handleInstallClick}
            data-testid="footer-install-app"
            title="Install the CADENCE app on this device"
          >
            <Smartphone size={14} />
            <span>Install App</span>
          </button>
        )}
        <button
          type="button"
          className={`app-action-btn ${isRefreshing ? 'refreshing' : ''} ${refreshDone ? 'done' : ''}`}
          onClick={handleRefresh}
          disabled={isRefreshing}
          data-testid="footer-refresh-app"
          title="Force the app to fetch the latest version"
        >
          {refreshDone ? <Check size={14} /> : <RefreshCw size={14} className={isRefreshing ? 'spin' : ''} />}
          <span>{refreshDone ? 'Updated' : isRefreshing ? 'Updating…' : 'Check for Updates'}</span>
        </button>
        {version && <span className="app-actions-version">{version}</span>}
      </div>

      {showIOSModal && (
        <div
          className="app-actions-modal-overlay"
          onClick={() => setShowIOSModal(false)}
          data-testid="install-instructions-modal"
        >
          <div className="app-actions-modal" onClick={(e) => e.stopPropagation()}>
            <button
              type="button"
              className="app-actions-modal-close"
              onClick={() => setShowIOSModal(false)}
              aria-label="Close"
            >
              <X size={20} />
            </button>
            <h3>Install CADENCE on your phone</h3>
            {isIOS ? (
              <ol className="install-steps">
                <li>
                  In Safari, tap the <Share2 size={16} className="inline-icon" /> <strong>Share</strong> button at the bottom.
                </li>
                <li>
                  Scroll down and tap <Plus size={16} className="inline-icon" /> <strong>Add to Home Screen</strong>.
                </li>
                <li>
                  Tap <strong>Add</strong> in the top right.
                </li>
                <li>Open CADENCE from the new icon on your Home Screen.</li>
              </ol>
            ) : (
              <ol className="install-steps">
                <li>Tap the menu (⋮) in your browser.</li>
                <li>
                  Choose <strong>Install app</strong> or <strong>Add to Home screen</strong>.
                </li>
                <li>Confirm to install. Open CADENCE from your Home screen / app drawer.</li>
              </ol>
            )}
            <p className="install-foot-note">
              Already installed? Tap <strong>Check for Updates</strong> to refresh to the latest version
              without uninstalling.
            </p>
          </div>
        </div>
      )}
    </>
  );
};

export default AppActions;
